import React, { useState, useEffect } from 'react';
import { 
  IconSearch, IconEdit, IconTrash, IconPlus, 
  IconSortAscending, IconSortDescending, IconCheck, IconX 
} from '@tabler/icons-react';
import { adminService } from '../../services/adminApi';
import styles from '../../pages/AdminDashboard.module.css';

interface Organization {
  id: number;
  name: string;
  description: string;
  email: string;
  website?: string;
  phone?: string;
  address?: string;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  eventCount?: number;
}

interface OrganizationManagementProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const OrganizationManagement: React.FC<OrganizationManagementProps> = ({ onSuccess, onError }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // Fetch organizations from API
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Appel API
        const response = await adminService.getAllOrganizations();
        
        // Assurer un format de données consistant
        if (Array.isArray(response)) {
          setOrganizations(response);
        } else if (response && typeof response === 'object') {
          // Utiliser un type assertion pour éviter les erreurs TypeScript
          const responseObj = response as Record<string, unknown>;
          
          if (responseObj.organizations && Array.isArray(responseObj.organizations)) {
            setOrganizations(responseObj.organizations);
          } else if (responseObj.data && Array.isArray(responseObj.data)) {
            setOrganizations(responseObj.data);
          } else {
            setOrganizations([]);
          }
        } else {
          setOrganizations([]);
        }
      } catch (err: any) {
        console.error('Erreur lors de la récupération des organisations:', err);
        setError('Impossible de récupérer les organisations. Veuillez réessayer plus tard.');
        if (onError) onError('Impossible de récupérer les organisations. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [onError]);

  const handleEdit = (organization: Organization) => {
    setCurrentOrganization(organization);
    setEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette organisation ? Cette action est irréversible.")) {
      try {
        setError(null);
        await adminService.deleteOrganization(id);
        
        // Update local state
        setOrganizations(organizations.filter(org => org.id !== id));
        if (onSuccess) onSuccess('Organisation supprimée avec succès.');
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'organisation:', error);
        setError('Impossible de supprimer l\'organisation. Veuillez réessayer plus tard.');
        if (onError) onError('Impossible de supprimer l\'organisation. Veuillez réessayer plus tard.');
      }
    }
  };

  const handleToggleVerification = async (id: number, isVerified: boolean) => {
    try {
      setError(null);
      await adminService.verifyOrganization(id, !isVerified);
      
      // Update local state
      setOrganizations(organizations.map(org => 
        org.id === id ? { ...org, isVerified: !isVerified } : org
      ));
      
      if (onSuccess) {
        const message = !isVerified 
          ? 'Organisation vérifiée avec succès.' 
          : 'Vérification retirée avec succès.';
        onSuccess(message);
      }
    } catch (error) {
      console.error('Erreur lors du changement de vérification:', error);
      setError('Impossible de modifier le statut de vérification. Veuillez réessayer plus tard.');
      if (onError) onError('Impossible de modifier le statut de vérification. Veuillez réessayer plus tard.');
    }
  };

  const handleSaveOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    try {
      setError(null);
      let updatedOrg;
      
      if (currentOrganization.id) {
        // Update existing organization
        updatedOrg = await adminService.updateOrganization(currentOrganization.id, currentOrganization);
        
        // Adapter les données reçues du backend 
        const formattedOrg = {
          ...updatedOrg,
          isVerified: updatedOrg._count?.events > 0 || currentOrganization.isVerified,
          eventCount: updatedOrg._count?.events || 0
        };
        
        setOrganizations(organizations.map(org => 
          org.id === currentOrganization.id ? formattedOrg : org
        ));
        
        if (onSuccess) onSuccess('Organisation mise à jour avec succès.');
      } else {
        // Create new organization
        updatedOrg = await adminService.createOrganization(currentOrganization);
        
        // Adapter les données reçues du backend
        const formattedOrg = {
          ...updatedOrg,
          isVerified: false, // Une nouvelle organisation n'est pas vérifiée par défaut
          isActive: true,
          eventCount: 0,
          createdAt: updatedOrg.createdAt || new Date().toISOString()
        };
        
        setOrganizations([...organizations, formattedOrg]);
        if (onSuccess) onSuccess('Organisation créée avec succès.');
      }
      
      setEditModalOpen(false);
      setCurrentOrganization(null);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'organisation:', error);
      setError('Impossible d\'enregistrer les modifications. Veuillez réessayer plus tard.');
      if (onError) onError('Impossible d\'enregistrer les modifications. Veuillez réessayer plus tard.');
    }
  };

  // Filter and sort organizations
  const filteredOrganizations = organizations
    .filter(org => 
      (org.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
       org.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (statusFilter === 'all' || 
       (statusFilter === 'verified' && org.isVerified) ||
       (statusFilter === 'unverified' && !org.isVerified) ||
       (statusFilter === 'active' && org.isActive) ||
       (statusFilter === 'inactive' && !org.isActive))
    )
    .sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc' 
          ? a.name.localeCompare(b.name) 
          : b.name.localeCompare(a.name);
      } else if (sortField === 'email') {
        return sortDirection === 'asc' 
          ? a.email.localeCompare(b.email) 
          : b.email.localeCompare(a.email);
      } else if (sortField === 'createdAt') {
        return sortDirection === 'asc' 
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() 
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else if (sortField === 'eventCount') {
        return sortDirection === 'asc'
          ? (a.eventCount || 0) - (b.eventCount || 0)
          : (b.eventCount || 0) - (a.eventCount || 0);
      }
      return 0;
    });

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2>Gestion des Organisations</h2>
        <button 
          className={styles.addButton}
          onClick={() => {
            setCurrentOrganization({
              id: 0,
              name: '',
              description: '',
              email: '',
              website: '',
              phone: '',
              address: '',
              isVerified: false,
              isActive: true,
              createdAt: new Date().toISOString()
            });
            setEditModalOpen(true);
          }}
        >
          <IconPlus size={16} />
          <span>Ajouter une organisation</span>
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <div className={styles.filterContainer}>
        <div className={styles.searchBar}>
          <IconSearch size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Rechercher par nom ou email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <select
          className={styles.select}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="verified">Vérifiées</option>
          <option value="unverified">Non vérifiées</option>
          <option value="active">Actives</option>
          <option value="inactive">Inactives</option>
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Chargement des organisations...</p>
        </div>
      ) : filteredOrganizations.length === 0 ? (
        <p className={styles.emptyMessage}>Aucune organisation trouvée.</p>
      ) : (
        <table className={styles.dataGrid}>
          <thead>
            <tr>
              <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                Nom {sortField === 'name' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer' }}>
                Email {sortField === 'email' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th>Description</th>
              <th onClick={() => toggleSort('eventCount')} style={{ cursor: 'pointer' }}>
                Événements {sortField === 'eventCount' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th>Statut</th>
              <th onClick={() => toggleSort('createdAt')} style={{ cursor: 'pointer' }}>
                Date de création {sortField === 'createdAt' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.map(org => (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td>{org.email}</td>
                <td>
                  <div className={styles.truncatedText}>
                    {org.description.substring(0, 70)}{org.description.length > 70 ? '...' : ''}
                  </div>
                </td>
                <td>{org.eventCount || 0}</td>
                <td>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <span className={`${styles.badge} ${org.isActive ? styles.badgeGreen : styles.badgeRed}`}>
                      {org.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`${styles.badge} ${org.isVerified ? styles.badgeBlue : styles.badgeGray}`}>
                      {org.isVerified ? 'Vérifiée' : 'Non vérifiée'}
                    </span>
                  </div>
                </td>
                <td>{new Date(org.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      className={styles.actionButton}
                      onClick={() => handleToggleVerification(org.id, org.isVerified)}
                      title={org.isVerified ? 'Retirer la vérification' : 'Vérifier l\'organisation'}
                    >
                      {org.isVerified ? <IconX size={16} /> : <IconCheck size={16} />}
                    </button>
                    <button 
                      className={styles.actionButton}
                      onClick={() => handleEdit(org)}
                      title="Modifier l'organisation"
                    >
                      <IconEdit size={16} />
                    </button>
                    <button 
                      className={styles.actionButton}
                      onClick={() => handleDelete(org.id)}
                      title="Supprimer l'organisation"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal for editing/creating organization */}
      {editModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {currentOrganization?.id ? 'Modifier l\'organisation' : 'Ajouter une nouvelle organisation'}
              </h3>
              <button 
                className={styles.closeButton}
                onClick={() => setEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            
            {currentOrganization && (
              <form onSubmit={handleSaveOrganization} className={styles.modalForm}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nom</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    required
                    value={currentOrganization.name}
                    onChange={(e) => setCurrentOrganization({...currentOrganization, name: e.target.value})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    required
                    value={currentOrganization.email}
                    onChange={(e) => setCurrentOrganization({...currentOrganization, email: e.target.value})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Description</label>
                  <textarea
                    className={styles.formTextarea}
                    value={currentOrganization.description}
                    onChange={(e) => setCurrentOrganization({...currentOrganization, description: e.target.value})}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Site Web (optionnel)</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={currentOrganization.website || ''}
                      onChange={(e) => setCurrentOrganization({...currentOrganization, website: e.target.value || undefined})}
                    />
                  </div>
                  
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Téléphone (optionnel)</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={currentOrganization.phone || ''}
                      onChange={(e) => setCurrentOrganization({...currentOrganization, phone: e.target.value || undefined})}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Adresse (optionnel)</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={currentOrganization.address || ''}
                    onChange={(e) => setCurrentOrganization({...currentOrganization, address: e.target.value || undefined})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <input
                      type="checkbox"
                      checked={currentOrganization.isActive}
                      onChange={(e) => setCurrentOrganization({...currentOrganization, isActive: e.target.checked})}
                    />
                    {' '}Organisation active
                  </label>
                </div>
                
                {currentOrganization.id > 0 && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <input
                        type="checkbox"
                        checked={currentOrganization.isVerified}
                        onChange={(e) => setCurrentOrganization({...currentOrganization, isVerified: e.target.checked})}
                      />
                      {' '}Organisation vérifiée
                    </label>
                  </div>
                )}
                
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setEditModalOpen(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={styles.saveButton}
                  >
                    Enregistrer
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
